import { useState, useEffect } from 'react';
import MarkdownEditor from '../shared/MarkdownEditor';
import EditSaveButtons from '../shared/EditSaveButtons';
import { adminApi } from '../../lib/admin-api';

export default function SystemPromptTab({ configuration, onUpdate, aiOverview }) {
  const [systemPrompt, setSystemPrompt] = useState(configuration?.system_prompt?.template || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  useEffect(() => {
    setSystemPrompt(configuration?.system_prompt?.template || '');
  }, [configuration]);

  // Load preview when not in edit mode
  useEffect(() => {
    if (!isEditMode && configuration?.id) {
      loadPreview();
    }
  }, [isEditMode, configuration?.id]);

  const loadPreview = async () => {
    if (!configuration?.id) return;

    setIsLoadingPreview(true);
    try {
      const preview = await adminApi.modelConfigurations.previewPrompts(configuration.id);
      setPreviewPrompt(preview.system_prompt || '');
    } catch (error) {
      console.error('Failed to load prompt preview:', error);
      setPreviewPrompt('Error loading preview. Please try again.');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleCancel = () => {
    setSystemPrompt(configuration?.system_prompt?.template || '');
    setIsEditMode(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updates = {
        main_system_prompt_template: systemPrompt
      };

      await onUpdate?.(updates);
      setIsEditMode(false);
    } catch (error) {
      console.error('Failed to save system prompt:', error);
      alert('Failed to save system prompt');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePromptSave = (newContent) => {
    setSystemPrompt(newContent);
  };

  return (
    <div className="p-8 max-w-5xl h-full flex flex-col">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between flex-shrink-0">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">System Prompt</h2>
          <p className="text-sm text-gray-400">
            {isEditMode
              ? 'Define the AI\'s behavior using templates with {{parameter}} placeholders'
              : 'Preview with actual parameter values from database'}
          </p>
        </div>

        {/* Edit/Save/Cancel Buttons */}
        <EditSaveButtons
          isEditMode={isEditMode}
          isSaving={isSaving}
          onEdit={() => setIsEditMode(true)}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </div>

      <div className="flex-1 flex flex-col min-h-0">

        {/* System Prompt Editor/Preview */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex items-baseline justify-between mb-3 flex-shrink-0">
            <label className="block text-sm font-semibold text-gray-200">
              {isEditMode ? 'Prompt Template (Edit Mode)' : 'Prompt Preview (Resolved)'}
            </label>
            {isEditMode && (
              <span className="text-xs text-gray-500">
                Use <span className="font-mono bg-gray-700/50 px-1.5 py-0.5 rounded">{`{{parameter_name}}`}</span> for dynamic content
              </span>
            )}
          </div>
          <div className="flex-1 min-h-0 relative">
            {isLoadingPreview && !isEditMode && (
              <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-10 rounded-lg">
                <div className="flex items-center gap-2 text-purple-400">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-sm font-medium">Loading preview...</span>
                </div>
              </div>
            )}
            <MarkdownEditor
              content={isEditMode ? systemPrompt : previewPrompt}
              onSave={handlePromptSave}
              externalControls={true}
              isEditing={isEditMode}
              textareaClassName="h-full"
              placeholder="Enter the system prompt template here..."
              showBorder={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
